import { useSnackbar } from 'notistack'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { RequestCommentType } from '@omnea-app/comments/commentsFrontendClient'
import { RequestCommentTypeEnum } from '@omnea-app/packages/universal/hasura/graphql'
import { UploadedFile } from '../uploadedFile'
import { useDeleteRequestComment } from './useDeleteRequestComment'
import { useEditRequestComment } from './useEditRequestComment'
import { RequestCommentsResponse, useGetRequestComment } from './useGetRequestComment'
import { useInsertRequestComment } from './useInsertRequestComment'
import { useMarkAmendmentsAsResolved } from './useMarkAmendmentsAsResolved'
import { useRequestAmendment } from './useRequestAmendment'

export type RequestCommentModel = NonNullable<RequestCommentsResponse['data']>[number]

export type AmendmentRequestInput = { message: string; files?: UploadedFile[]; questionTitle?: string }

export type InsertCommentInput = {
  id: string
  payload: { text: string; files?: UploadedFile[] }
  isInternalComment?: boolean
}

export type ThreadInfo = {
  requestId: string
  requestFormId?: string
  requestFormTemplateId?: string
  questionId?: string
  taskId?: string
  requestStepId?: string
  rfxId?: string
}

export type HandleAddComment = (input: {
  newComment: InsertCommentInput
  formId?: string
  requestFormTemplateId?: string
  questionId?: string
  taskId?: string
  rfxId?: string
  rfxSupplierId?: string
  type?: RequestCommentType
  requestStepId?: string
  integration?: string
  parentCommentId?: string
}) => Promise<void>

type CommentsMutations = {
  handleAddComment: HandleAddComment
  handleUpdateComment: (input: InsertCommentInput) => Promise<void>
  handleRemoveComment: (commentId: string) => Promise<void>
  requestAmendment: (input: AmendmentRequestInput) => Promise<void>
  handleMarkAmendmentsAsResolved: (commentId: string[]) => Promise<void>
  addUnrejectRequestComment: (input: { reason: string }) => Promise<void>
  addRejectRequestComment: (input: { reason: string }) => Promise<void>
  addPauseComment: (input: { reason: { text: string }; note: string | null }) => Promise<void>
  addResumeComment: () => Promise<void>
}
type CommentsData = {
  comments: RequestCommentModel[]
  loading: boolean
}

type GetRequestCommentsOptions<T extends boolean> = {
  // if enabled = true, the comments will be fetched and returned
  enabled: T
  refetchInterval?: number
}

type GetRequestCommentsType<T extends boolean> = T extends true ? CommentsMutations & CommentsData : CommentsMutations

export function useRequestComments<T extends boolean>(
  { requestId, requestFormId, questionId, taskId, requestStepId, rfxId }: ThreadInfo,
  options?: GetRequestCommentsOptions<T>
): GetRequestCommentsType<T> {
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation('requests')
  const onCommentError = useCallback(() => enqueueSnackbar(t('comments.post-error'), { variant: 'error' }), [enqueueSnackbar, t])
  const onAmendmentError = useCallback(
    () => enqueueSnackbar(t('comments.amendment-error'), { variant: 'error' }),
    [enqueueSnackbar, t]
  )
  const { data: commentsData, isLoading: loading } = useGetRequestComment(
    { requestId, requestFormId, questionId, rfxId },
    { skip: !options?.enabled, refetchInterval: options?.refetchInterval }
  )
  const { mutateAsync: insertRequestComment } = useInsertRequestComment({
    requestId,
    onError: onCommentError,
  })
  const { mutateAsync: editRequestComment } = useEditRequestComment({ requestId })
  const { mutateAsync: deleteRequestComment } = useDeleteRequestComment({ requestId })

  const { requestAmendment: handleRequestAmendment } = useRequestAmendment({
    requestId,
    onError: onAmendmentError,
  })
  const markAmendmentsAsResolved = useMarkAmendmentsAsResolved({ requestId })

  const addRejectRequestComment = async ({ reason }: { reason: string }) => {
    await insertRequestComment({
      requestId,
      payload: { text: reason },
      type: RequestCommentTypeEnum.REJECTION,
    })
  }

  const addUnrejectRequestComment = async ({ reason }: { reason: string }) => {
    await insertRequestComment({
      requestId,
      payload: { text: reason },
      type: RequestCommentTypeEnum.UNREJECTION,
    })
  }

  const addPauseComment = async ({ reason, note }: { reason: { text: string }; note: string }) => {
    await insertRequestComment({
      requestId,
      payload: { text: `${reason.text}.${note ? `\n${note}` : ''}` },
      type: RequestCommentTypeEnum.PAUSE,
    })
  }

  const addResumeComment = async () => {
    await insertRequestComment({
      requestId,
      payload: { text: '' },
      type: RequestCommentTypeEnum.RESUME,
    })
  }

  const handleAddComment: HandleAddComment = async ({
    newComment,
    formId,
    questionId,
    requestFormTemplateId,
    taskId,
    rfxId,
    rfxSupplierId,
    type = 'TEXT',
    requestStepId,
    integration,
    parentCommentId,
  }) => {
    await insertRequestComment({
      requestId,
      requestFormId: formId,
      questionId,
      requestFormTemplateId,
      taskId,
      rfxId,
      rfxSupplierId,
      payload: newComment.payload,
      type,
      isInternalComment: Boolean(newComment.isInternalComment),
      requestStepId,
      integration,
      parentCommentId,
    })
  }

  const handleUpdateComment = async (editedComment: InsertCommentInput) => {
    await editRequestComment({ id: editedComment.id, payload: editedComment.payload })
  }

  const handleRemoveComment = async (commentId: string) => {
    await deleteRequestComment(commentId)
  }

  const requestAmendment = async (amendmentRequestInput: AmendmentRequestInput) => {
    if (!requestId || !requestFormId || !questionId) {
      return
    }
    await handleRequestAmendment({ requestFormId, questionId, requestStepId, taskId }, amendmentRequestInput)
  }

  const handleMarkAmendmentsAsResolved = async (commentIds: string[]) => {
    if (!requestFormId) {
      return
    }
    await markAmendmentsAsResolved({ commentIds, requestFormId })
  }

  /**
   * 1. When only requestId is passed, BE returns all comments for the request.
   * 2. When only request & requestFormId is passed, BE returns all comments for the request form (including question comments).
   * 3. When request, requestFormId & questionId is passed, BE returns all comments for the question.
   * For case 1 & 3, this the expected behavior in the FE, but for case 2 we only want to display the form-level comments in the FormHeader's CommentWidget.
   * Basically we didn't sacrifice the consistency of the data returned by BE to fullfil watch FE needs.
   */
  const comments = commentsData || []

  if (!options?.enabled) {
    return {
      handleAddComment,
      handleUpdateComment,
      handleRemoveComment,
      requestAmendment,
      handleMarkAmendmentsAsResolved,
      addUnrejectRequestComment,
      addRejectRequestComment,
      addPauseComment,
      addResumeComment,
    } as GetRequestCommentsType<T>
  }

  return {
    comments,
    handleAddComment,
    handleUpdateComment,
    handleRemoveComment,
    requestAmendment,
    handleMarkAmendmentsAsResolved,
    addUnrejectRequestComment,
    addRejectRequestComment,
    addPauseComment,
    addResumeComment,
    loading,
  } as GetRequestCommentsType<T>
}
